import { toDiscount } from '@/api/mappers/mapDiscount';
import { toOrderUnresolvedIncompleteDelivery } from '@/api/mappers/mapOrderIncompleteDelivery';
import { toStore } from '@/api/mappers/mapStore';
import { toSupplier } from '@/api/mappers/mapSupplier';
import {
  DailyOrderMetricsResult as ApiDailyOrderMetricsResult,
  MonthlyOrderMetricsResult as ApiMonthlyOrderMetricsResult,
  Order as ApiOrder,
  OrderCreate as ApiOrderCreate,
  OrderItem as ApiOrderItem,
  OrderItemCreate as ApiOrderItemCreate,
  OrderListResponse as ApiOrderListResponse,
  OrderStatus as ApiOrderStatus,
} from '@/gen/api';
import { OrderCreate, Order as OrderModel, Orders } from '@/models/Order';
import { OrderItemCreate, OrderItem as OrderItemModel } from '@/models/OrderItem';
import { DailyOrderMetricsResult, MonthlyOrderMetricsResult } from '@/models/OrderMetrics';
import { OrderStatus } from '@/models/OrderStatus';

export const parseOrderStatus = (status: string): OrderStatus => {
  switch (status.toUpperCase()) {
    case 'NEW':
      return OrderStatus.New;
    case 'ACCEPTED':
      return OrderStatus.Accepted;
    case 'DISPATCHED':
      return OrderStatus.Dispatched;
    case 'REJECTED':
      return OrderStatus.Rejected;
    case 'COMPLETED':
      return OrderStatus.Completed;
    case 'CANCELED':
      return OrderStatus.Canceled;
    case 'SUBMITTED':
      return OrderStatus.Submitted;
    case 'RECEIVED':
      return OrderStatus.Received;
    default:
      return OrderStatus.Unknown;
  }
};

export const toOrder = (apiOrder: ApiOrder): OrderModel => {
  return {
    ...apiOrder,
    items: apiOrder.items.map(toOrderItem),
    status: parseOrderStatus(apiOrder.status),
    store: apiOrder.store && toStore(apiOrder.store),
    supplier: apiOrder.supplier ? toSupplier(apiOrder.supplier) : undefined,
    rejectReason: apiOrder.rejectReason || '',
    unresolvedIncompleteDelivery: toOrderUnresolvedIncompleteDelivery(
      apiOrder.unresolvedIncompleteDelivery,
    ),
  };
};

export const toOrderItem = (apiOrderItem: ApiOrderItem): OrderItemModel => {
  return {
    ...apiOrderItem,
    promotions: apiOrderItem.promotions?.map((promotion) => {
      const discounts = promotion.discounts.map(toDiscount);
      return {
        ...promotion,
        discounts,
      };
    }),
  };
};

export const toOrders = (response: ApiOrderListResponse): Orders => {
  return {
    items: response.items.map(toOrder),
    totalCount: response.totalCount,
    estimatedTotalCount: response.estimatedTotalCount,
  };
};

const toApiOrderItemCreate = (item: OrderItemCreate): ApiOrderItemCreate => {
  return { ...item };
};

export const toApiOrderCreate = (order: OrderCreate): ApiOrderCreate => {
  return {
    ...order,
    note: order.note || '',
    items: order.items.map(toApiOrderItemCreate),
  };
};

const orderStatusMap: Record<OrderStatus, ApiOrderStatus> = {
  [OrderStatus.New]: ApiOrderStatus.New,
  [OrderStatus.Accepted]: ApiOrderStatus.Accepted,
  [OrderStatus.Dispatched]: ApiOrderStatus.Dispatched,
  [OrderStatus.Rejected]: ApiOrderStatus.Rejected,
  [OrderStatus.Completed]: ApiOrderStatus.Completed,
  [OrderStatus.Canceled]: ApiOrderStatus.Canceled,
  [OrderStatus.Submitted]: ApiOrderStatus.Submitted,
  [OrderStatus.Received]: ApiOrderStatus.Received,
  [OrderStatus.Unknown]: ApiOrderStatus.Unknown,
};

export const toApiOrderStatus = (orderStatus: string | OrderStatus): ApiOrderStatus | undefined => {
  if (typeof orderStatus === 'string') {
    const matchedKey = Object.values(OrderStatus).find((val) => val === orderStatus);
    if (matchedKey && matchedKey in orderStatusMap) {
      return orderStatusMap[matchedKey];
    }
  } else {
    if (orderStatus in orderStatusMap) {
      return orderStatusMap[orderStatus];
    }
  }

  return;
};

export const toDailyOrderMetricsResult = (
  apiDailyOrderMetricsResult: ApiDailyOrderMetricsResult,
): DailyOrderMetricsResult => {
  return { ...apiDailyOrderMetricsResult };
};

export const toMonthlyOrderMetricsResult = (
  apiMonthlyOrderMetricsResult: ApiMonthlyOrderMetricsResult,
): MonthlyOrderMetricsResult => {
  return { ...apiMonthlyOrderMetricsResult };
};
